import { z } from "zod";
import { MechanicalNoteSchema, ProseSchema } from "./prose.js";
import { FactIdSchema, FidelitySchema, MoveTypeSchema, RubricDimensionSchema } from "./primitives.js";
/**
 * THE LEAD (structure-and-rewards phase; ADR 0008). A lead is one authored
 * PROMISE / PROGRESS / PAYOFF arc (Sanderson), enforced by the Three Clue
 * Rule (>= 3 promises across >= 2 channels per payoff) and paid out by a
 * DETERMINISTIC behavioural gate — see the ADR for why the gate is engine
 * code and not a model judgment.
 *
 * Everything here is AUTHORED case content validated at load; nothing is
 * generated at play time. `title` and every MechanicalNote field are
 * author-facing and never rendered; Prose fields are learner-deliverable.
 */
export const PromiseChannelSchema = z.enum(["DOCUMENT_LINE", "NPC_LINE", "MARGIN_NOTE", "ENVIRONMENT", "OVERHEARD"]);
export const PromiseSubtletySchema = z.enum(["LOUD", "PLAIN", "QUIET"]);
/**
 * Where a promise physically lives. A document anchor surfaces when the
 * learner OPENS that document (not merely receives it); an NPC anchor
 * surfaces when shipped narration actually carries the promise's text; an
 * environment anchor (ADR 0009) surfaces when the named fixture first
 * renders its promising state in the section (e.g. the lit exit sign) —
 * recorded PROMISE_SURFACED by the orchestrator, like every other channel.
 */
const PromiseAnchorSchema = z.discriminatedUnion("kind", [
    z.strictObject({ kind: z.literal("document"), doc_id: z.string().min(1), block_hint: z.string().min(1) }),
    z.strictObject({ kind: z.literal("npc"), npc: z.string().min(1), topic: z.string().min(1) }),
    z.strictObject({ kind: z.literal("environment"), env_id: z.string().min(1), fixture: z.string().min(1) }),
]);
export const LeadPromiseSchema = z.strictObject({
    id: z.string().min(1),
    channel: PromiseChannelSchema,
    anchor: PromiseAnchorSchema,
    /**
     * For document/margin anchors: the EXACT authored line already present in
     * the document (used for verification and for the dev-panel board). For
     * NPC/ENVIRONMENT/OVERHEARD anchors: the authored line the narrator is
     * directed to weave, verbatim or near-verbatim, when the ladder or an
     * interview brings this promise up.
     */
    text: z.string().min(1),
    /** The payoff this foreshadows — the machine-checkable link. */
    points_at: z.string().min(1),
    subtlety: PromiseSubtletySchema,
});
/**
 * A payoff yield is always diegetic (Law 2): a document, a better copy of a
 * document, or a scene. Facts ride documents (Law 3) — every yield names a
 * doc_id whose declared_fact_ids are the facts this payoff actually pays.
 * ENVIRONMENT yields are documents of kind ENVIRONMENT (a lived scene,
 * captured as the learner's own field impressions).
 */
export const PayoffYieldSchema = z.discriminatedUnion("kind", [
    z.strictObject({ kind: z.literal("DOCUMENT"), doc_id: z.string().min(1), fidelity: FidelitySchema }),
    z.strictObject({ kind: z.literal("FIDELITY_UPGRADE"), doc_id: z.string().min(1), to: FidelitySchema }),
    z.strictObject({ kind: z.literal("ENVIRONMENT"), doc_id: z.string().min(1) }),
]);
/**
 * PAYOFFS ARE BOUND TO QUESTIONS, NOT ROUTES: the same yield is authored in
 * channel-appropriate voices. `reveal` is the acknowledgement moment — the
 * narrator MUST deliver it (or its substance) so a payoff is perceptibly a
 * payoff.
 */
const PayoffVariantSchema = z.strictObject({
    /** Which kind of learner move this voice fits (interview, data request, site visit...). */
    via: z.enum(["INTERVIEW", "DATA_REQUEST", "SITE_VISIT", "ANALYSIS", "ANY"]),
    reveal: ProseSchema,
});
export const LeadPayoffSchema = z.strictObject({
    id: z.string().min(1),
    /** Author-facing: the case question this payoff answers. */
    question: MechanicalNoteSchema,
    yield: PayoffYieldSchema,
    variants: z.array(PayoffVariantSchema).min(2).max(4),
    /** The learner-voice notes lines (progress surface 4a). */
    notes_open: ProseSchema,
    notes_answered: ProseSchema,
});
/**
 * A recognisable move that advances the lead — DETERMINISTIC matchers over
 * the declared intent, not a model judgment (ADR 0008). `signals` are
 * lowercase stems, >= 1 must appear in the learner's move for the lead to be
 * PULLED; `specificity_signals` separate HIT from NEAR — the authored
 * "missing specificity" a NEAR nudge then names.
 */
export const LeadProgressSchema = z.strictObject({
    id: z.string().min(1),
    description: MechanicalNoteSchema,
    /** Which triage/move shapes qualify. REQUEST_DOCUMENT and ACT are the common ones. */
    intents: z.array(z.enum(["ACT", "REQUEST_DOCUMENT", "STATE_FRAMEWORK"])).min(1),
    /** ACT move types that qualify, when intent is ACT. Empty = any. */
    move_types: z.array(MoveTypeSchema),
    signals: z.array(z.string().min(2)).min(1),
    specificity_signals: z.array(z.string().min(2)),
});
/**
 * The behavioural gate — a conjunction of machine-checkable conditions.
 * `behaviour_note` states, in rubric-anchor language, what demonstrated
 * behaviour this gate recognises; the ASSESSOR scores the same dimensions
 * post-hoc (trains[]), so reward and assessment stay one value system
 * while the in-turn yield decision stays deterministic (Law 1).
 */
export const LeadGateSchema = z.strictObject({
    behaviour_note: MechanicalNoteSchema,
    prerequisites: z.strictObject({
        facts: z.array(FactIdSchema),
        documents: z.array(z.string().min(1)),
    }),
    /**
     * Who could actually yield this — NPC names and/or channel words. A move
     * matching progress signals but addressed to nobody plausible fails
     * channel_fit: the near-miss is recorded, the lead's `near_nudge` is not
     * spoken, and the redirect and the nudge ladder answer a nameless ask, not
     * the line (Andre, 2026-10-28, answer 1: 19A over 19B's channel clause).
     * Empty = any addressee.
     */
    channel_fit: z.array(z.string().min(1)),
    /** Optional freshness window, as beat indices into caseBible.beats (0-based, inclusive). */
    freshness: z.strictObject({ from_beat: z.number().int().min(0), to_beat: z.number().int().min(0) }).nullable(),
});
export const LeadToleranceSchema = z.strictObject({
    /**
     * The in-fiction NEAR nudge from a named person — must name the missing specificity. It is
     * spoken only on a specificity near-miss with one of its speakers addressed: never on a turn
     * that addresses nobody, never on channel_fit, depends_on, prerequisite or freshness (Andre,
     * 2026-10-27, C3-U2; 2026-10-28, answer 1: 19A over 19B's channel clause; `nearNudgeSpeaksOn`
     * and `nearNudgeWithheld` in packages/engine/src/leads.ts). Its speakers are the person its own
     * words name, else the people `channel_fit` names, else the lead's holder
     * (`nearNudgeSpeakers`); a line none of them gives a speaker is never spoken.
     */
    near_nudge: ProseSchema,
    /** Optional smaller yield on a NEAR. */
    near_partial: PayoffYieldSchema.nullable(),
});
export const LeadDecaySchema = z.strictObject({
    /** The lead decays if unpulled when this beat index (0-based) is reached. */
    after_beat: z.number().int().min(0),
    mode: z.enum(["DEBRIEF_COUNTERFACTUAL", "NPC_ACTS_FIRST", "SURFACES_AT_COST"]),
    /** Author-facing note; for DEBRIEF_COUNTERFACTUAL this is the reveal shown at debrief. */
    note: ProseSchema,
    /**
     * THE WORLD MOVES (ADR 0020, idea 2). The one speakable line the engine may put in the
     * scene when an NPC_ACTS_FIRST or SURFACES_AT_COST lead's holder acts on a neglected
     * thread after a miss. `note` is author-facing and often written in retrospect ("nobody
     * opens the model, so the model sizes itself"), and one carries a figure, so it is never
     * narrated; this line is. Codex writes it; case:check holds it to prose, no digits, and
     * no more than two content words shared with any fact. Absent means the lead's world-move
     * stays switched off.
     */
    line: ProseSchema.optional(),
    /**
     * Who acts when this lead is neglected (stage 3). The engine derives a holder from
     * `gate.channel_fit` or the payoff document's author; a lead whose payoff the learner builds
     * has neither, so its author names the person here. A client in the cast, never the mentor or
     * the examiner (case:check V55). Read by code only; leads never reach a model's view.
     */
    holder: z.string().min(1).optional(),
});
export const LeadSchema = z.strictObject({
    id: z.string().min(1),
    /** Authoring-facing only — never rendered. */
    title: z.string().min(1),
    /** Beat indices (0-based, inclusive) this lead is live in. */
    beat_window: z.strictObject({ from_beat: z.number().int().min(0), to_beat: z.number().int().min(0) }),
    promises: z.array(LeadPromiseSchema).min(3),
    progress: z.array(LeadProgressSchema).min(1),
    payoff: LeadPayoffSchema,
    gate: LeadGateSchema,
    tolerance: LeadToleranceSchema,
    depends_on: z.array(z.string().min(1)),
    trains: z.array(RubricDimensionSchema).min(1),
    /**
     * THE TECHNIQUE, IN LEARNER-FACING WORDS — what earning this payoff
     * demonstrated, phrased as capability ("pressure-tested a headline number
     * against its source ledger"). Rendered on the practice record.
     *
     * Authored separately from `gate.behaviour_note` because that note is a
     * MechanicalNote carrying rubric-anchor vocabulary — right content, wrong
     * register, and Law 2 forbids rendering it. Nullable so existing synthetic
     * bibles stay valid; a lead without one simply never appears in the
     * demonstrated repertoire.
     */
    demonstrated: ProseSchema.nullable().default(null),
    decay: LeadDecaySchema,
    /** Hugh's rung-2 nudge — the promise volunteered as a question, in his voice. */
    mentor_nudge: ProseSchema,
});
/**
 * AN AUTHORED OPENING (ADR 0020, stage 3; idea 2 phase 2). Something the world may put in
 * front of a learner whose turn went nowhere: a clue from elsewhere, the other side showing a
 * card, a rival calling, a price being named. The engine chooses among the legal ones by a
 * fixed order and never draws; a model never chooses one and never sees the table. An opening
 * that delivers paper delivers it at a degraded fidelity, never CLEAN: a clean copy is earned.
 * "The holder acts first" and "the door closes" are not kinds here; they are a lead's decay
 * and the arc's bargain, already authored.
 */
export const OpeningKindSchema = z.enum(["CLUE_FROM_ELSEWHERE", "ANTAGONIST_SHOWS_A_CARD", "RIVAL_CALLS", "PRICE_NAMED"]);
export const OpeningSchema = z.strictObject({
    id: z.string().min(1),
    kind: OpeningKindSchema,
    /** The person in the cast who speaks or sends it. Never the mentor or the examiner (case:check V60). */
    holder: z.string().min(1),
    /** The lead this opening points back to, when it has one. */
    thread: z.string().min(1).nullable(),
    delivers: z.strictObject({ doc_id: z.string().min(1), fidelity: z.enum(["PARTIAL", "CONTESTED", "STALE", "AGGREGATED"]) }).nullable(),
    when: z.strictObject({
        from_beat: z.number().int().min(0),
        to_beat: z.number().int().min(0),
        requires_held: z.array(z.string().min(1)).default([]),
        requires_not_held: z.array(z.string().min(1)).default([]),
        /**
         * The arc phases after which the premise is false (the reading pass of 2026-10-27, CM-7): once
         * the log records any of them entered, the opening is not legal. A world change's call
         * (RIVAL_CALLS, drafted as `op_<phase>_first`) lists the phases that supersede the change, its
         * `blockedBy`; a paper a superseding phase only fires on no longer stands in for one. case:check
         * V60 refuses an id the arc does not carry. Optional with no default, read as `?? []`: every
         * published edition resolves and parses to the same JSON (a default would add the key to every
         * opening, and case:seed would republish every case), and the turn reads a stored row's bible
         * without parsing it, so a row published before the field has no key to read.
         */
        not_after_phases: z.array(z.string().min(1)).optional(),
    }),
    once: z.literal(true),
    /** The one speakable line. Codex writes it; held to the same restatement rule as a world-move line. */
    line: ProseSchema,
    /** Writing direction for the narrator. Rides the beat, never the printed directive. */
    performance: MechanicalNoteSchema,
});
/** A bible fact that is deliberately scenery — not a payoff, not opening desk — with the author's reason. */
export const SceneryFactSchema = z.strictObject({
    fact_id: FactIdSchema,
    reason: MechanicalNoteSchema,
});
//# sourceMappingURL=lead.js.map